import React from "react";
import courses from "./data";
import star from "../../../Assets/Homeimgs/star.svg";
import { useNavigate } from "react-router-dom";

function Card({ course, navigate }) {
  const rating = course?.details?.rating ?? course?.rating ?? 0;
  const fullStars = Math.round(rating);

  return (
    <div data-aos="zoom-in-up" className="group relative">
      {/* Card glow */}
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-tr from-[#064585]/40 via-[#5989C8]/20 to-transparent opacity-60 blur-2xl group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative h-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-4 flex flex-col shadow-xl group-hover:-translate-y-1 group-hover:shadow-2xl transition-all duration-300">
        {/* Image */}
        <div className="relative overflow-hidden rounded-2xl mb-4">
          <img
            src={course.details.image}
            alt={course.details.name}
            className="w-full h-56 object-cover rounded-2xl"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-3 flex-1">
          <h2 className="text-lg md:text-xl font-semibold text-white line-clamp-2">
            {course.details.name}
          </h2>

          {/* Price */}
          <div className="flex items-baseline gap-2">
            <span className="text-xl md:text-2xl font-bold text-emerald-400">
              ₹{course.details.price}
            </span>
            <span className="text-sm md:text-base text-slate-300/80 line-through">
              ₹{course.details.originalPrice}
            </span>
          </div>

          {/* Meta info */}
          <div className="grid grid-cols-3 gap-2 text-[11px] sm:text-xs text-slate-200/90">
            <div className="rounded-xl border border-white/10 bg-white/5 px-2 py-1.5 text-center">
              <p className="font-semibold text-white">
                {course.details.lesson}
              </p>
              <p className="opacity-80">Lessons</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 px-2 py-1.5 text-center">
              <p className="font-semibold text-white">
                {course.details.assignment}
              </p>
              <p className="opacity-80">Assignments</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 px-2 py-1.5 text-center">
              <p className="font-semibold text-white">
                {course.details.enrolled}
              </p>
              <p className="opacity-80">Enrolled</p>
            </div>
          </div>

          {/* Rating + Button */}
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {Array.from({ length: fullStars }).map((_, i) => (
                  <img
                    key={i}
                    src={star}
                    alt="rating star"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                ))}
              </div>
              <span className="text-xs sm:text-sm text-slate-200/90">
                {rating} ({course.details.reviews})
              </span>
            </div>

            <button
              onClick={() => {
                navigate(`/courses/${course.id}`);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              type="button"
              className="inline-flex items-center justify-center rounded-full bg-[#064585] px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-[#064585]/40 hover:bg-[#033465] transition-all duration-200 group-hover:-translate-y-0.5"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const FeaturedCourses = () => {
  const navigate = useNavigate();

  return (
    <section className="relative overflow-hidden py-10 lg:py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Background glows */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -right-32 -top-24 h-72 w-72 rounded-full bg-[#064585]/40 blur-3xl" />
        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#5989C8]/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="space-y-2">
            <div
              className="inline-flex items-center justify-center md:justify-start px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm text-xs sm:text-sm text-[#5989C8] font-medium"
              data-aos="fade-up"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400 mr-2" />
              What’s New
            </div>

            <div data-aos="fade-down-right">
              <h1 className="text-2xl md:text-4xl font-semibold text-white">
                Featured{" "}
                <span className="bg-gradient-to-r from-[#64a2ff] to-[#064585] bg-clip-text text-transparent">
                  Courses
                </span>
              </h1>
              <p className="mt-2 text-sm md:text-base text-slate-200/90 max-w-xl">
                Learn the most in-demand skills with our curated,
                beginner-friendly and advanced courses designed for students and
                professionals.
              </p>
            </div>
          </div>

          {/* Optional View All button (enable if needed) */}
          <div data-aos="fade-left">
            <button
              className="inline-flex items-center justify-center rounded-full bg-white/5 border border-white/10 px-5 py-2 text-sm font-semibold text-white hover:bg-white/10 transition-all duration-200"
              onClick={() => {
                navigate("/courses");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              View All Courses
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
          {courses.map((course, index) => (
            <Card key={index} course={course} navigate={navigate} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCourses;
